import { isEqual } from 'lodash';
import isNumeric from 'validator/es/lib/isNumeric';
import {
  invalidUrlCharacters,
  translatedError,
} from '@/utils/errors';
import {
  hasMinimumLength, hasKey, findMatch,
} from '@/utils/resultsHelper';
import fields, {
  globalFieldLengths,
} from '@/constants/fields';
import urlDenyCharacters from '@/constants/validation';
import { findWithUUID } from '@/utils/uuidHelper';
import { cohortPermissions } from './constants';
import { removeUUIDsFromQueries } from './queries/state/state';
import validateQuery, { queriesDiff } from './queries/helper';

export const cohortChanged = ({
  name,
  description,
  queries,
  copy,
}) => {
  if (name !== copy.name
      || description !== copy.description) {
    return true;
  }

  const sourceQueries = removeUUIDsFromQueries({ queries });
  const targetQueries = removeUUIDsFromQueries(copy);

  return !isEqual(sourceQueries, targetQueries);
};

const cohortDiff = (cohort) => {
  const {
    name, description, queries, copy,
  } = cohort;
  const diff = {
    name: name === copy.name ? null : name,
    description: description === copy.description ? null : description,
    queries: queriesDiff(queries, copy.queries),
  };

  Object.keys(diff).forEach((k) => diff[k] == null && delete diff[k]);

  return Object.keys(diff).length > 0 ? diff : null;
};

export const canEditCohort = ({ permissions, isNew }, role) => {
  const { cohorts } = role.permissions;
  return isNew ? cohorts.create : cohorts.update && permissions[cohortPermissions.UPDATE];
};

export const getCohortActiveDictionary = (cohort) => {
  if (!cohort.queries?.length) {
    return null;
  }
  const { queries, activeQueryUUID } = cohort;
  const query = findWithUUID(queries, activeQueryUUID);
  return findMatch(query.dataset.dictionaries, query.activeDictionaryCode);
};

export const isFilterField = (dictionaryField) => (dictionaryField.cohort_filter);

export const filterableFields = (dictionaryFields) => (dictionaryFields.filter(isFilterField));

const validateCohortCode = (code) => {
  const errors = [];
  if ((isNumeric(code) || invalidUrlCharacters(code))) {
    errors.push(translatedError(
      'cohort',
      'save.cohort.validation.code.title',
      'validation.invalid.code',
      [fields.CODE],
      { characters: urlDenyCharacters },
    ));
  }
  return errors;
};

const validateCohortName = (name) => {
  const errors = [];
  if (!hasMinimumLength(name)) {
    errors.push(translatedError(
      'cohort',
      'save.cohort.validation.required.name.title',
      'save.cohort.validation.required.name',
      [fields.NAME],
    ));
  }

  if (name.length > globalFieldLengths.name) {
    errors.push(translatedError(
      'cohort',
      'save.cohort.validation.name.title',
      'validation.max.length',
      [fields.NAME],
      { maximum: globalFieldLengths.name },
    ));
  }
  return errors;
};

const validateCohortDescription = (description) => {
  const errors = [];
  if (description.length > globalFieldLengths.description) {
    errors.push(translatedError(
      'cohort',
      'save.cohort.validation.description.title',
      'validation.max.length',
      [fields.DESCRIPTION],
      { maximum: globalFieldLengths.name },
    ));
  }
  return errors;
};

const validateCohortQueries = (queries) => {
  const errors = [];
  const notNewQueries = queries.filter((q) => !q.isNew);
  if (notNewQueries.length === 0) {
    errors.push(translatedError(
      'cohort',
      'save.cohort.validation.required.queries.title',
      'save.cohort.validation.required.queries',
    ));
  }

  notNewQueries.forEach((query) => validateQuery(query, errors));
  return errors;
};

export const validateCohort = (cohort) => {
  const errors = [];

  if (cohort.isNew && hasKey(cohort, 'code') && cohort.code) {
    errors.push(...validateCohortCode(cohort.code));
  }

  if (hasKey(cohort, 'name')) {
    errors.push(...validateCohortName(cohort.name));
  }

  if (hasKey(cohort, 'description')) {
    errors.push(...validateCohortDescription(cohort.description));
  }

  if (hasKey(cohort, 'queries')) {
    errors.push(...validateCohortQueries(cohort.queries));
  }

  return errors;
};

const replaceDuplicates = (arr, match, replace) => {
  const [filtered, matches] = arr.reduce((result, element) => {
    result[element === match ? 1 : 0].push(element);
    return result;
  }, [[], []]);

  if (matches.length <= 1) {
    return arr;
  }

  filtered.push(replace);
  return filtered;
};

export const cohortReadError = (errors, title = 'read.cohort.validation.invalid') => {
  let details = errors.map(({ message }) => message);
  [
    { match: 'required.filter.field', replace: 'required.filter.fields' },
    { match: 'mismatch.filter.operator', replace: 'mismatch.filter.operators' },
    { match: 'required.clause.dictionary', replace: 'required.clause.dictionaries' },
  ].forEach(({ match, replace }) => {
    details = replaceDuplicates(details, `read.cohort.validation.${match}`, `read.cohort.validation.${replace}`);
  });

  return ({
    title,
    context: errors[0].context,
    details,
    translation: { on: true, details: { on: true } },
  });
};

export const systemCohortReadError = (errors, isSystemCohort) => (
  cohortReadError(errors, isSystemCohort ? 'read.cohort.validation.invalid.system' : errors[0].title)
);

export default cohortDiff;
